/**
 * Privacy notice banner for the Nova chat widget.
 *
 * Displays a dismissable notice informing the user about data processing.
 * Dismissal state is persisted in localStorage so the notice only shows once.
 */

const STORAGE_KEY = 'nova_privacy_dismissed';

/**
 * Check whether the user has previously dismissed the privacy notice.
 */
export function isPrivacyDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // localStorage may be unavailable (e.g. incognito, iframe sandbox)
    return false;
  }
}

/**
 * Create the privacy notice DOM element.
 *
 * @param message - Translated notice text (from i18n).
 * @param onDismiss - Callback invoked after the notice is dismissed.
 * @returns A div with class `nova-privacy-notice` and `role="alert"`.
 */
export function createPrivacyNotice(message: string, onDismiss: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'nova-privacy-notice';
  container.setAttribute('role', 'alert');

  const textSpan = document.createElement('span');
  textSpan.className = 'nova-privacy-notice__text';
  textSpan.textContent = message;

  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'nova-privacy-notice__dismiss';
  dismissBtn.setAttribute('aria-label', 'Dismiss privacy notice');
  dismissBtn.setAttribute('type', 'button');
  dismissBtn.textContent = '\u00D7'; // multiplication sign (x)

  dismissBtn.addEventListener('click', () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Silently ignore if localStorage is unavailable
    }
    container.remove();
    onDismiss();
  });

  container.appendChild(textSpan);
  container.appendChild(dismissBtn);

  return container;
}
